import { useEffect, useState } from "react";
import { AdminHeader } from "../../components/header";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { formatMoney, NAIRA_SYMBOL } from "../../utils";
import { doc, DocumentData, getDoc } from "firebase/firestore";
import { firebaseAuth, firestoredb } from "../../utils/firebase";
import { signOut } from "firebase/auth";

export function RequestPayoutScreen({
  balance,
  eventData,
}: {
  balance: number;
  eventData: DocumentData | null;
}) {
  // this is a mobile screen only
  const navigate = useNavigate();
  return (
    <div className='request-payout-screen mobile'>
      <AdminHeader />
      <div className='request-payout-screen__header'>
        <div
          className='back-navigation'
          onClick={() => navigate("/admin/dashboard")}
        >
          <i className='bi bi-arrow-left'></i>
        </div>
        <h1>Request Payout</h1>
      </div>
      <RequestPayoutComponent balance={balance} eventData={eventData} />
    </div>
  );
}

export function RequestPayoutComponent({
  balance,
  eventData,
}: {
  balance: number;
  eventData: DocumentData | null;
}) {
  const [payoutAmountRequested, setPayoutAmountRequested] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { eventId } = useParams();

  const [data, setData] = useState<DocumentData | null>(null);

  useEffect(() => {
    if (eventData) return setData(eventData);
    const getEventData = async () => {
      const eventRef = doc(firestoredb, "events", eventId ?? "");
      const eventSnap = await getDoc(eventRef);
      if (!eventSnap.exists()) {
        toast.error("Event not found");
        await signOut(firebaseAuth);
        localStorage.clear();
        window.location.href = "/admin/login";
      }
      setData({ id: eventSnap.id, ...eventSnap.data() });
    };
    getEventData();
  }, [eventData, eventId]);

  const handleConfirmPayout = async () => {
    if (!balance)
      return toast.error("You do not have a balance to withdraw from");
    if (payoutAmountRequested > balance)
      return toast.error("Invalid Amount inputted.");
    try {
      setIsLoading(true);
      // send email
      await axios.post(
        process.env.REACT_APP_EMAIL_API!,
        {
          sender_email: "noreply@usedingpay.com",
          sender_name: "DingPay",
          email: "main@usedingpay.com",
          message_type: "email",
          subject: "Payout Request",
          html: `<p>A payout request has been made for <b>₦${formatMoney(
            payoutAmountRequested
          )} NGN</b> for <b>${data?.title ?? "event"}</b></p>`,
        },
        {
          headers: {
            "X-Authorization": process.env.REACT_APP_EMAIL_API_KEY,
          },
        }
      );
      toast.success("Payout request sent successfully");
      window.location.href = "/admin/dashboard";
    } catch (error) {
      console.log(error);
      toast.error("Error sending payout request");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='request-payout-screen__container'>
      <div className='form-input-group'>
        <label htmlFor='payout-amount'>
          Amount <span>*</span>
        </label>
        <input
          id='payout-amount'
          placeholder='Enter amount'
          value={`${NAIRA_SYMBOL} ${formatMoney(payoutAmountRequested)}`}
          onChange={({ target: { value } }) => {
            const cleanedValue = Number(value.replace(/[^0-9]/g, ""));
            setPayoutAmountRequested(Number(cleanedValue));
          }}
        />
      </div>
      <p>
        <span>Available Balance:</span> ₦{formatMoney(balance)}
      </p>
      <button
        disabled={payoutAmountRequested === 0 || isLoading || !data}
        onClick={handleConfirmPayout}
        className={`${isLoading ? "loading" : ""}`}
      >
        {isLoading ? "Requesting..." : "Request"}
      </button>
    </div>
  );
}

export default RequestPayoutScreen;
